import { useTranslation } from 'react-i18next';
import type { Profile } from '../../lib/auth.ts';
import { ChipGroup, NumberField, SaveButton, SelectField, TextListField } from './fields.tsx';
import {
  DIETARY_PREFERENCES,
  NUTRITION_GOALS,
  SEX_OPTIONS,
  SPORT_OPTIONS,
  parseCsv,
  toggle,
} from './profileOptions.ts';

const SELECTED_CHIP = 'bg-emerald-500/20 text-emerald-400 border-emerald-500';

export function NutritionTab({
  profile, setProfile, allergiesText, setAllergiesText, excludedFoodsText, setExcludedFoodsText,
  error, saving, saveLabel, onSave,
}: Readonly<{
  profile: Profile;
  setProfile: (p: Profile) => void;
  // Raw text for the comma-separated inputs; see ProfilePage.
  allergiesText: string;
  setAllergiesText: (s: string) => void;
  excludedFoodsText: string;
  setExcludedFoodsText: (s: string) => void;
  error: string;
  saving: boolean;
  saveLabel: string;
  onSave: () => void;
}>) {
  const { t } = useTranslation();
  const translate = (group: string, options: readonly { id: string }[]) =>
    options.map((o) => ({ id: o.id, label: t(`profile.options.${group}.${o.id}`) }));
  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-(--color-text-secondary)">
        {t('profile.nutrition.intro')}
      </p>

      <ChipGroup
        legend={t('profile.nutrition.sex')}
        options={translate('sex', SEX_OPTIONS)}
        isSelected={(id) => profile.sex === id}
        onToggle={(id) => setProfile({ ...profile, sex: profile.sex === id ? null : id })}
        selectedClass={SELECTED_CHIP}
      />

      <NumberField
        label={t('profile.nutrition.age')}
        value={profile.age}
        onChange={(v) => setProfile({ ...profile, age: v })}
        min={10}
        max={100}
      />

      <SelectField
        id="profile-primary-sport"
        label={t('profile.nutrition.primarySport')}
        placeholder={t('profile.nutrition.selectSport')}
        value={profile.primary_sport}
        options={translate('sport', SPORT_OPTIONS)}
        onChange={(v) => setProfile({ ...profile, primary_sport: v })}
      />

      <NumberField
        label={t('profile.nutrition.weeklyHours')}
        value={profile.weekly_training_hours}
        onChange={(v) => setProfile({ ...profile, weekly_training_hours: v })}
        min={0}
        max={60}
      />

      <SelectField
        id="profile-nutrition-goal"
        label={t('profile.nutrition.goal')}
        placeholder={t('profile.nutrition.selectGoal')}
        value={profile.goal}
        options={translate('nutritionGoal', NUTRITION_GOALS)}
        onChange={(v) => setProfile({ ...profile, goal: v })}
      />

      <ChipGroup
        legend={t('profile.nutrition.dietaryPreferences')}
        options={translate('diet', DIETARY_PREFERENCES)}
        isSelected={(id) => profile.dietary_preferences?.includes(id) ?? false}
        onToggle={(id) => {
          const next = toggle(profile.dietary_preferences, id);
          setProfile({ ...profile, dietary_preferences: next.length > 0 ? next : null });
        }}
        selectedClass={SELECTED_CHIP}
      />

      <TextListField
        id="profile-allergies"
        label={t('profile.nutrition.allergies')}
        placeholder={t('profile.nutrition.allergiesPlaceholder')}
        value={allergiesText}
        onChange={(text) => {
          setAllergiesText(text);
          setProfile({ ...profile, allergies: parseCsv(text) });
        }}
      />

      <TextListField
        id="profile-excluded-foods"
        label={t('profile.nutrition.excludedFoods')}
        placeholder={t('profile.nutrition.excludedFoodsPlaceholder')}
        value={excludedFoodsText}
        onChange={(text) => {
          setExcludedFoodsText(text);
          setProfile({ ...profile, excluded_foods: parseCsv(text) });
        }}
      />

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <SaveButton
        onClick={onSave}
        disabled={saving}
        label={saveLabel}
        colorClass="bg-emerald-400 shadow-emerald-500/30"
      />
    </div>
  );
}
